import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { doc, Timestamp } from 'firebase/firestore';
import { setDoc, deleteDoc } from '../config/escrituras';
import { auth, db } from '../config/firebase';
import { URL_PUBLICA } from '../config/marca';
import { esApp } from './plataforma';

/**
 * Notificaciones al móvil (solo la app de iOS). Hoy las usan los
 * recordatorios de A medias, que manda cada tarde la función de Netlify
 * recordatorios-push.
 *
 * Cada iPhone con las notificaciones activadas deja su token de Apple en
 * usuarios/{uid}/dispositivos/{token}. El token se guarda también en el propio
 * iPhone para saber que están activadas aquí y poder darlo de baja.
 */

const CLAVE = 'tokenPush';

export type EstadoNotificaciones = 'activadas' | 'desactivadas' | 'denegadas' | 'no-disponible';

export async function estadoNotificaciones(): Promise<EstadoNotificaciones> {
  if (!esApp) return 'no-disponible';
  const permiso = await PushNotifications.checkPermissions();
  if (permiso.receive === 'denied') return 'denegadas';
  const { value } = await Preferences.get({ key: CLAVE });
  return value && permiso.receive === 'granted' ? 'activadas' : 'desactivadas';
}

/** Pide a iOS el token del dispositivo (llega por evento, no como respuesta). */
function pedirToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const limpiar: Array<() => void> = [];
    const fin = () => limpiar.forEach(f => f());
    const tiempo = setTimeout(() => { fin(); reject(new Error('iOS no ha respondido')); }, 20_000);
    limpiar.push(() => clearTimeout(tiempo));
    PushNotifications.addListener('registration', t => { fin(); resolve(t.value); })
      .then(h => limpiar.push(() => h.remove()));
    PushNotifications.addListener('registrationError', e => { fin(); reject(new Error(e.error)); })
      .then(h => limpiar.push(() => h.remove()));
    PushNotifications.register().catch(e => { fin(); reject(e); });
  });
}

async function guardarToken(uid: string, token: string) {
  await setDoc(doc(db, 'usuarios', uid, 'dispositivos', token), {
    plataforma: 'ios',
    actualizado: Timestamp.now(),
  });
  await Preferences.set({ key: CLAVE, value: token });
}

/** Pide permiso (la primera vez sale el aviso de iOS) y registra este iPhone. */
export async function activarNotificaciones(uid: string): Promise<EstadoNotificaciones> {
  const permiso = await PushNotifications.requestPermissions();
  if (permiso.receive !== 'granted') return 'denegadas';
  await guardarToken(uid, await pedirToken());
  return 'activadas';
}

/** Da de baja este iPhone. También al cerrar sesión: que no le lleguen avisos de otro. */
export async function desactivarNotificaciones(uid: string): Promise<void> {
  if (!esApp) return;
  const { value } = await Preferences.get({ key: CLAVE });
  if (value) await deleteDoc(doc(db, 'usuarios', uid, 'dispositivos', value)).catch(() => {});
  await Preferences.remove({ key: CLAVE });
  await PushNotifications.unregister().catch(() => {});
}

/**
 * Al abrir la app: si estaban activadas, se vuelve a pedir el token, porque
 * iOS puede cambiarlo (al restaurar el iPhone, al reinstalar la app…).
 */
export async function refrescarToken(uid: string): Promise<void> {
  if ((await estadoNotificaciones()) !== 'activadas') return;
  const { value: anterior } = await Preferences.get({ key: CLAVE });
  const token = await pedirToken();
  if (anterior && anterior !== token) {
    await deleteDoc(doc(db, 'usuarios', uid, 'dispositivos', anterior)).catch(() => {});
  }
  await guardarToken(uid, token);
}

/**
 * Pide al servidor que mande una notificación a este iPhone. Sirve para
 * comprobar la configuración de APNs; devuelve el mensaje para enseñar.
 */
export async function enviarPrueba(): Promise<{ ok: boolean; mensaje: string }> {
  const { value: token } = await Preferences.get({ key: CLAVE });
  const usuario = auth.currentUser;
  if (!token || !usuario) return { ok: false, mensaje: 'Activa primero las notificaciones.' };
  let r: Response;
  try {
    r = await fetch(`${URL_PUBLICA}/.netlify/functions/push-prueba`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await usuario.getIdToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch (e) {
    return { ok: false, mensaje: `No se ha podido contactar con el servidor (${String(e)}).` };
  }
  try {
    const datos = await r.json();
    return { ok: !!datos.ok, mensaje: datos.mensaje || datos.error || 'No se ha podido enviar.' };
  } catch {
    return { ok: false, mensaje: `El servidor ha contestado ${r.status} sin datos: ¿está publicada la función push-prueba?` };
  }
}
